<template>
	<label
		v-if="label || hasFieldActions"
		class="form-label"
		:class="{ 'd-flex align-items-center': hasFieldActions }"
		:for="uid"
	>
		<span v-if="label">{{ t(label) }}{{ isRequired ? '* ' : '' }}</span>
		<efPopover v-if="popover" :title="t(`${popover}.title`)" :text="t(`${popover}.text`)" />
		<span v-if="hasFieldActions" class="ms-auto d-flex align-items-center gap-1">
			<button
				v-if="canReset"
				type="button"
				class="btn btn-link btn-sm p-0 lh-1"
				:title="t(resetTitle)"
				@click.prevent="resetField"
			>
				<i class="fa fa-fw fa-undo" />
			</button>
			<button
				v-if="canFill"
				type="button"
				class="btn btn-link btn-sm p-0 lh-1"
				:title="t(fillTitle)"
				@click.prevent="fillField"
			>
				<i class="fa fa-fw fa-copy" />
			</button>
		</span>
	</label>
	<div
		class="input-group"
		:class="[{ 'has-validation': !isVisiblyValid }, props.size ? `input-group-${props.size}` : '']"
	>
		<div v-if="!!slots['input-group-prepend']" class="input-group-text">
			<slot name="input-group-prepend"></slot>
		</div>
		<input
			:id="uid"
			v-model.trim="model"
			@focus="autofillGuard = false"
			@input="onInput"
			@blur="onBlur"
			@change="onChange"
			@paste="onPaste"
			:type="props.type"
			:maxlength="props.maxlength"
			:placeholder="props.placeholder"
			:class="['form-control', { 'is-invalid': !isVisiblyValid }, props.class]"
			:disabled="props.disabled"
			:readonly="(props.preventAutofill && autofillGuard) || props.readonly"
			:autocomplete="autocomplete"
			:name="uid"
		/>
		<div v-if="!!slots['input-group-append']" class="input-group-text">
			<slot name="input-group-append"></slot>
		</div>
		<slot></slot>
	</div>
	<div v-if="visibleValidations.length > 0" class="invalid-feedback d-block">
		{{ visibleValidations.map((key) => t(`validations.${key}`)).join(', ') }}
	</div>
</template>

<script setup lang="ts">
import { computed, ref, useSlots, type HTMLAttributes } from 'vue';
import { useI18n } from 'vue-i18n';

import efPopover from '@lib/components/efPopover.vue';
import useUid from '@lib/composables/useUid';
import useValidations from '@lib/composables/useValidations';

/*
 * States
 */
const { t } = useI18n();
const slots = useSlots();
const uid = useUid();

const props = withDefaults(
	defineProps<{
		readonly?: boolean;
		disabled?: boolean;
		/**
		 * For a field holding somebody else's data, e.g. a law firm's address: read only until it is
		 * focused, so the browser does not fill in the person's own, and - while `autocomplete` is
		 * `off` - a token that names no address field, so Firefox does not offer to save it as theirs.
		 */
		preventAutofill?: boolean;
		type?: string;
		label?: string;
		popover?: string;
		maxlength?: string | number;
		placeholder?: string;
		class?: HTMLAttributes['class'];
		/** `sm` or `lg`, the Bootstrap sizes; on the group, so a prepend or append follows. */
		size?: 'sm' | 'lg';
		autocomplete?: string;
		validations?: Record<string, (item: ModelType) => boolean>;
		transform?: (value: ModelType) => ModelType;
		required?: boolean;
		/** Offers a reset beside the label that puts `resetValue` back, while the field differs. */
		resettable?: boolean;
		/** What the reset writes. Empty is what makes a field fall back to what it inherits. */
		resetValue?: ModelType;
		/** Locale key of its tooltip, so a caller can say what the field falls back to. */
		resetTitle?: string;
		/** Offers a copy of the placeholder into the field, while the field is empty. */
		fillable?: boolean;
		/** Locale key of its tooltip, so a caller can say what is being copied in. */
		fillTitle?: string;
	}>(),
	{
		readonly: false,
		disabled: false,
		preventAutofill: false,
		type: 'text',
		autocomplete: 'off',
		required: false,
		resettable: false,
		resetValue: null,
		resetTitle: 'buttons.generic.reset',
		fillable: false,
		fillTitle: 'buttons.generic.fill'
	}
);

const autofillGuard = ref(true);

/**
 * Firefox ignores "off" when it captures addresses and guesses the field from its label; a valid
 * token that names no address field is taken as the field's type instead, and no browser fills
 * anything for it. Do not make this "off": tested in Firefox, "off" brings back the offer to save
 * a law firm's address as the user's. Held by __tests__/efInput.test.ts.
 */
const autocomplete = computed<string>(() =>
	props.preventAutofill && props.autocomplete === 'off' ? 'transaction-amount' : props.autocomplete,
);

type ModelType = string | number | null;

const model = defineModel<ModelType>();

/*
 * Validation
 */
const { isRequired, visibleValidations, isVisiblyValid, touch } = useValidations<ModelType>({
	uid,
	value: model,
	validations: () => props.validations,
	required: () => props.required
});

/*
 * Reset and fill
 *
 * Both act on the field, so they belong with its label. A click on interactive content in a label
 * never reaches the labelled control, so neither of them touches the input itself.
 */
/** Empty and unset are the same to a field: both say it holds no value of its own. */
function isEmpty(value: ModelType | undefined): boolean {
	return value === null || value === undefined || value === '';
}

/** Nothing to put back while the field already holds what a reset would write. */
const canReset = computed<boolean>(() => {
	if (!props.resettable) return false;

	return isEmpty(model.value) && isEmpty(props.resetValue) ? false : model.value !== props.resetValue;
});

/** Only worth offering where the field is empty and its placeholder says what it would hold. */
const canFill = computed<boolean>(
	() => props.fillable && isEmpty(model.value) && !!props.placeholder,
);

const hasFieldActions = computed<boolean>(() => canReset.value || canFill.value);

function resetField(): void {
	model.value = props.resetValue;
}

/** Start from the value the placeholder shows rather than from a blank field. */
function fillField(): void {
	model.value = props.placeholder ?? null;
}

/*
 * Emits
 */
const emit = defineEmits<{
	input: [value: ModelType];
	blur: [value: ModelType];
	change: [value: ModelType];
	/** Before the field takes the paste; a listener that cancels it has taken the paste itself. */
	paste: [event: ClipboardEvent];
}>();

const onBlur = () => {
	touch();
	emit('blur', model.value ?? null);
};
const onChange = () => emit('change', model.value ?? null);

/*
 * Functions
 */
const onInput = (e: Event) => {
	const raw = (e.target as HTMLInputElement).value;
	model.value = props.transform ? props.transform(raw) : raw;
	emit('input', model.value);
};

const onPaste = (e: ClipboardEvent) => {
	emit('paste', e);
	if (e.defaultPrevented) return;
	e.preventDefault();
	const pasted = e.clipboardData?.getData('text') ?? '';
	model.value = props.transform ? props.transform(pasted) : pasted.trim();
};
</script>
